import { useEffect } from 'react';
import type { RefObject } from 'react';
import { getClearSelection } from '../core';
import type { Viewport } from '../core';
import { useStoreApi } from '../store';
import type { MindmapStoreApi } from '../store';
import { isInsideClass, NO_PAN_CLASS, suppressNextClick } from '../utils';

const PRIMARY_BUTTON = 0;
const PANNING_CLASS = 'cm-renderer--panning';
const PANE_CLASS = 'cm-pane';

type PanStart = { clientX: number; clientY: number; viewport: Viewport; isOnPane: boolean; hasMoved: boolean };

const clearSelection = (store: MindmapStoreApi) => {
  const { nodes, edges, applySelection } = store.getState();
  applySelection(getClearSelection(nodes, edges));
};

/**
 * Dragging with the primary button pans; `.nopan` elements and ctrl+drag do not (v11). Document-level listeners
 * instead of pointer capture keep the click target intact, so clicks on edges and nodes still reach them.
 * A press and release on the pane without moving is a pane click: it clears the selection.
 */
export const useDragPan = (rendererRef: RefObject<HTMLElement>): void => {
  const store = useStoreApi();
  useEffect(() => {
    const renderer = rendererRef.current;
    if (!renderer) return undefined;
    const doc = renderer.ownerDocument;
    let start: PanStart | null = null;

    const handlePointerMove = (event: PointerEvent) => {
      if (!start) return;
      const { viewport, clientX, clientY } = start;
      if (event.clientX !== clientX || event.clientY !== clientY) start.hasMoved = true;
      store.getState().setViewport({ ...viewport, x: viewport.x + event.clientX - clientX, y: viewport.y + event.clientY - clientY });
    };
    const handlePointerUp = () => {
      doc.removeEventListener('pointermove', handlePointerMove);
      doc.removeEventListener('pointerup', handlePointerUp);
      renderer.classList.remove(PANNING_CLASS);
      if (start?.hasMoved && doc.defaultView) suppressNextClick(doc.defaultView);
      else if (start?.isOnPane) clearSelection(store);
      start = null;
    };
    const handlePointerDown = (event: PointerEvent) => {
      // Shift+drag is box select (useBoxSelect); Ctrl+drag does nothing (v11 d3 filter).
      if (event.button !== PRIMARY_BUTTON || event.ctrlKey || event.shiftKey || isInsideClass(event.target, NO_PAN_CLASS)) return;
      start = {
        clientX: event.clientX,
        clientY: event.clientY,
        viewport: store.getState().viewport,
        isOnPane: isInsideClass(event.target, PANE_CLASS),
        hasMoved: false,
      };
      renderer.classList.add(PANNING_CLASS);
      doc.addEventListener('pointermove', handlePointerMove);
      doc.addEventListener('pointerup', handlePointerUp);
    };

    renderer.addEventListener('pointerdown', handlePointerDown);
    return () => {
      renderer.removeEventListener('pointerdown', handlePointerDown);
      doc.removeEventListener('pointermove', handlePointerMove);
      doc.removeEventListener('pointerup', handlePointerUp);
    };
  }, [rendererRef, store]);
};
